import { useState, useEffect, useRef } from "react";
import { motion, useScroll, useTransform, AnimatePresence, useReducedMotion } from "framer-motion";
import { Check, ClipboardCheck, BrainCircuit, Flower2, Watch, Armchair, Activity, BriefcaseMedical, HardHat, Leaf, HeartPulse, ShieldCheck, Sparkles } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "@/components/site/Reveal";
import { ASSETS } from "./assets";

const ItemList = ({ items }) => (
  <Stagger className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
    {items.map((it) => (
      <StaggerItem key={it}>
        <span className="flex items-center gap-2.5 text-sm font-semibold text-slate-700">
          <span className="h-5 w-5 rounded-full bg-[#2ab5cf]/15 flex items-center justify-center shrink-0">
            <Check size={12} strokeWidth={3} className="text-[#2ab5cf]" />
          </span>
          {it}
        </span>
      </StaggerItem>
    ))}
  </Stagger>
);

/* PEHC — rotating ID card */
const PehcScene = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const rotate = useTransform(scrollYProgress, [0, 1], [-8, 8]);
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);
  return (
    <div ref={ref} className="relative grid grid-cols-1 lg:grid-cols-2 gap-12 items-center py-10 md:py-14" data-testid="service-pehc">
      <Reveal>
        <h3 className="font-display text-[#12294d] text-3xl md:text-5xl tracking-tight mt-5 leading-[1.1]">
          Pre-Employment <span className="text-[#2ab5cf]">Health Check-up (PEHC)</span>
        </h3>
        <p className="mt-6 text-slate-600 leading-relaxed max-w-lg">
          Comprehensive medical evaluation to determine an individual's fitness before joining employment.
        </p>
      </Reveal>
      <Reveal delay={0.15}>
        <motion.img
          style={{ rotate, y }}
          src={ASSETS.idCard} alt="3D employee medical clearance ID card"
          className="w-[70%] max-w-sm mx-auto scene-blend floaty-slow" loading="lazy" data-testid="pehc-idcard-img"
        />
      </Reveal>
    </div>
  );
};

/* AHC — employee with sequential health rings */
const Ring = ({ r, color, value, duration, reverse = false, reducedMotion }) => {
  const c = 2 * Math.PI * r;
  return (
    <g>
      <circle cx="150" cy="150" r={r} fill="none" stroke={color} strokeOpacity="0.15" strokeWidth="10" />
      <motion.circle
        cx="150" cy="150" r={r} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - value)}
        initial={{ rotate: 0 }}
        animate={reducedMotion ? { rotate: 0 } : { rotate: reverse ? -360 : 360 }}
        transition={reducedMotion ? { duration: 0 } : { duration, repeat: Infinity, ease: "linear" }}
        style={{ transformOrigin: "150px 150px" }}
        transform="rotate(-90 150 150)"
      />
    </g>
  );
};

const AhcScene = () => {
  const reducedMotion = useReducedMotion();
  return (
    <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-12 items-center py-10 md:py-14" data-testid="service-ahc">
      <div className="relative flex items-center justify-center order-2 lg:order-1">
        <svg viewBox="0 0 300 300" className="absolute w-[88%] max-w-md" aria-hidden="true">
          <Ring r={140} color="#e8a7a4" value={0.78} duration={85} reducedMotion={reducedMotion} />
          <Ring r={122} color="#88d3d8" value={0.64} duration={105} reverse reducedMotion={reducedMotion} />
          <Ring r={104} color="#a7b8d8" value={0.86} duration={95} reducedMotion={reducedMotion} />
        </svg>
        <img src={ASSETS.employeeStanding} alt="Employee with annual health metrics" className="h-[52vh] max-h-[460px] w-auto scene-blend relative" loading="lazy" data-testid="ahc-employee-img" />
      </div>
      <Reveal className="order-1 lg:order-2">
        <h3 className="font-display text-[#12294d] text-3xl md:text-5xl tracking-tight mt-5 leading-[1.1]">
          Annual <span className="text-[#2ab5cf]">Health Check-up (AHC)</span>
        </h3>
        <p className="mt-6 text-slate-600 leading-relaxed max-w-lg">
          Customized health screening packages designed for early detection and preventive healthcare.
        </p>
      </Reveal>
    </div>
  );
};

/* MENTAL HEALTH — head with cycling states */
const mentalStates = ["Stress", "Balance", "Focus", "Wellbeing"];
const MentalScene = () => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % mentalStates.length), 2600);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center py-10 md:py-14" data-testid="service-mental">
      <Reveal>
        <h3 className="font-display text-[#12294d] text-3xl md:text-5xl tracking-tight mt-5 leading-[1.1]">
          Mental Health & <span className="text-[#2ab5cf]">Wellbeing</span>
        </h3>
        <p className="mt-6 text-slate-600 leading-relaxed max-w-lg">
          Stress and anxiety management workshops along with confidential 1:1 counseling sessions. We provide ADHD, Depression counselling as well.
        </p>
        <ItemList items={["Stress and anxiety management workshops", "Confidential 1:1 counseling sessions", "ADHD", "Depression counselling"]} />
      </Reveal>
      <Reveal delay={0.15}>
        <div className="relative flex flex-col items-center">
          <img src={ASSETS.mentalHead} alt="Abstract 3D representation of mental wellbeing" className="w-[72%] max-w-sm scene-blend breathe" loading="lazy" data-testid="mental-head-img" />
          <div className="h-10 mt-2 relative w-full text-center" data-testid="mental-state-word">
            <AnimatePresence mode="wait">
              <motion.span
                key={idx}
                initial={{ opacity: 0, y: 14, filter: "blur(6px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                exit={{ opacity: 0, y: -14, filter: "blur(6px)" }}
                transition={{ duration: 0.7 }}
                className="absolute inset-x-0 font-display text-2xl tracking-[0.25em] uppercase text-[#1a3a6b]"
              >
                {mentalStates[idx]}
              </motion.span>
            </AnimatePresence>
          </div>
        </div>
      </Reveal>
    </div>
  );
};

/* NUTRITION */
const NutritionScene = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [60, -30]);
  return (
    <div ref={ref} className="relative grid grid-cols-1 lg:grid-cols-2 gap-12 items-center py-10 md:py-14" data-testid="service-nutrition">
      <Reveal>
        <motion.img
          style={{ y }}
          src={ASSETS.nutrition} alt="Balanced 3D nutrition and lifestyle composition"
          className="w-[78%] max-w-md mx-auto scene-blend" loading="lazy" data-testid="nutrition-img"
        />
      </Reveal>
      <Reveal delay={0.15}>
        <h3 className="font-display text-[#12294d] text-3xl md:text-5xl tracking-tight mt-5 leading-[1.1]">
          Clinical Nutrition & <span className="text-[#2ab5cf]">Lifestyle Management</span>
        </h3>
        <p className="mt-6 text-slate-600 leading-relaxed max-w-lg">
          Where medical findings indicate lifestyle-related health risks, MedVerify provides individualized wellness interventions.
        </p>
        <ItemList items={["Weight Management", "Diabetes Management", "Hypertension Counselling", "Lifestyle Coaching"]} />
      </Reveal>
    </div>
  );
};

 

const moreServices = [
  { title: "Aptitude Testing & Personality Assessment", desc: "Aptitude testing and role-specific pre-employment health screenings to ensure the right fit from day one. These assessments assess personality and acumen required for the role.", icon: ClipboardCheck, accent: BrainCircuit },
  { title: "Holistic Wellbeing Services", desc: "We offer life-transformative wellness practices such as 1. Art of Living sessions 2. Yoga therapy.", icon: Flower2, accent: Sparkles },
  { title: "Smart Ring Technology", desc: "Our state of art smart ring technology is a game changer. Allows for clients’ HR to track and monitor employees’ health parameters. This allows to take preventative medical measures to help avoid the individual’s health condition getting worse.", icon: Watch, accent: HeartPulse },
  { title: "Workplace Ergonomics", desc: "We offer workplace ergonomic assessments focused on reducing work-related injuries, improving employee comfort, and enhancing productivity.", icon: Armchair, accent: Activity },
  { title: "Physiotherapy", desc: "Our physiotherapy services include injury rehabilitation, pain management, and mobility improvement to support employee health and wellbeing.", icon: Activity, accent: HeartPulse },
  { title: "First Aid Training", desc: "We conduct practical first aid training programs to equip employees with essential emergency response skills, strengthen workplace safety, and improve emergency preparedness.", icon: BriefcaseMedical, accent: ShieldCheck },
  { title: "Occupational Health & Safety", desc: "Train your staff on how to deal with workplace or factory injuries or emergencies like fire.", icon: HardHat, accent: ShieldCheck },
  { title: "ESG – Environmental, Social and Governance Advisory", desc: "We can provide expert advisory services on ESG responsibilities of organizations.", icon: Leaf, accent: ShieldCheck },
];
const uploadedServiceImages = {
  "Workplace Ergonomics": "/assets/Ergonomic%20Workspace%20Infographic%20Setup.png",
  "Physiotherapy": "/assets/Physiotherapy%20Rehab%20Equipment%20Scene.png",
  "First Aid Training": "/assets/3D%20CPR%20Training%20Scene%20with%20First%20Aid%20Gear.png",
  "Occupational Health & Safety": "/assets/Workplace_Safety_Briefing_with_PPE-removebg-preview.png",
  "ESG – Environmental, Social and Governance Advisory": "/assets/3D_ESG_Sustainability_Consulting_Scene-removebg-preview.png"
};
const SmartRingVisual = () => {
  const reducedMotion = useReducedMotion();
  return (
    <div className="relative w-full max-w-[380px] mx-auto flex items-center justify-center" aria-label="Smart ring with animated health monitoring signals">
      <motion.div
        className="absolute inset-x-[16%] top-[28%] h-[44%] rounded-full bg-[#bceaf0]/30 blur-3xl pointer-events-none"
        animate={reducedMotion ? undefined : { opacity: [0.35, 0.7, 0.35], scale: [0.95, 1.07, 0.95] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.img
        src="/assets/Smart%20Ring%20Health%20Dashboard%20Orbit.png"
        alt="3D smart health-monitoring ring"
        className="relative w-full h-auto drop-shadow-[0_15px_18px_rgba(18,41,77,0.12)]"
        initial={{ opacity: 0, y: 22 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        animate={reducedMotion ? undefined : { y: [0, -7, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute top-[18%] right-[10%] rounded-full bg-white/90 border border-[#c4e9e8] p-2 shadow-sm text-[#35aeb7]"
        animate={reducedMotion ? undefined : { y: [0, -5, 0], opacity: [0.65, 1, 0.65] }}
        transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
      ><HeartPulse size={22} strokeWidth={1.5} /></motion.div>
      <motion.div
        className="absolute bottom-[18%] left-[8%] rounded-full bg-[#f3e8fa]/90 border border-[#e9d7f1] p-2 shadow-sm text-[#9476ad]"
        animate={reducedMotion ? undefined : { y: [0, 5, 0], opacity: [0.6, 1, 0.6] }}
        transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
      ><Activity size={20} strokeWidth={1.5} /></motion.div>
    </div>
  );
};

const serviceTitleParts = {
  "Aptitude Testing & Personality Assessment": ["Aptitude Testing &", "Personality Assessment"],
  "Holistic Wellbeing Services": ["Holistic", "Wellbeing Services"],
  "Smart Ring Technology": ["Smart Ring", "Technology"],
  "Workplace Ergonomics": ["Workplace", "Ergonomics"],
  "Physiotherapy": ["Physio", "therapy"],
  "First Aid Training": ["First Aid", "Training"],
  "Occupational Health & Safety": ["Occupational Health &", "Safety"],
  "ESG – Environmental, Social and Governance Advisory": ["ESG – Environmental, Social and", "Governance Advisory"]
};

const ServiceTitle = ({ title }) => {
  const parts = serviceTitleParts[title];
  return parts ? <>{parts[0]} <span className="text-[#2ab5cf]">{parts[1]}</span></> : title;
};

const AdditionalServices = ({ services, offset = 0 }) => (
  <div>
    {services.map((service, i) => (
      <div key={service.title}>
        {!service.desc ? (
          <Reveal className="flex items-center gap-4 border-b border-slate-200 py-4 md:py-5" >
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#e7f6fb]">
              <service.icon size={25} strokeWidth={1.6} className="text-[#198bb4]" />
            </div>
            <h3 className="font-display text-[#12294d] text-xl md:text-2xl tracking-tight"><ServiceTitle title={service.title} /></h3>
          </Reveal>
        ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-8 items-center py-7 md:py-9 border-b border-slate-200" data-testid={`additional-service-${i + offset}`}>
          <Reveal className={(i + offset) % 2 ? "lg:order-2" : ""}>
            <h3 className="font-display text-[#12294d] text-3xl md:text-5xl tracking-tight leading-[1.1]"><ServiceTitle title={service.title} /></h3>
            {service.title === "Holistic Wellbeing Services" ? (
              <div className="mt-6 text-slate-600 leading-relaxed max-w-lg">
                <p>We offer life-transformative wellness practices such as</p>
                <ol className="mt-3 list-decimal pl-6 space-y-2">
                  <li><strong>Art of Living sessions</strong> — You can leverage this program to practice Sudarshan Kriya - the ancient and time tested practice of rhythmic breathing technique that clears physical, emotional stress, energizes you, and is a truly life transformative experience.</li>
                  <li><strong>Yoga therapy</strong></li>
                </ol>
              </div>
            ) : service.desc ? <p className="mt-6 text-slate-600 leading-relaxed max-w-lg">{service.desc}</p> : null}
          </Reveal>
          <Reveal delay={0.15} className={(i + offset) % 2 ? "lg:order-1" : ""}>
            <div className="relative min-h-[170px] md:min-h-[210px] flex items-center justify-center overflow-visible" aria-hidden="true">
              {service.title === "Smart Ring Technology" ? <SmartRingVisual /> : (["Holistic Wellbeing Services", "Aptitude Testing & Personality Assessment"].includes(service.title) || uploadedServiceImages[service.title]) ? (
                <motion.img
                  src={uploadedServiceImages[service.title] || (service.title === "Holistic Wellbeing Services" ? "/assets/holistic-yoga-3d.png" : service.title === "Aptitude Testing & Personality Assessment" ? "/assets/Pastel_Aptitude_Skills_Assessment_Scene-removebg-preview.png" : "/assets/Smart%20Ring%20Health%20Dashboard%20Orbit.png")}
                  alt=""
                  className="relative w-full max-w-[380px] h-auto drop-shadow-[0_15px_18px_rgba(18,41,77,0.12)]"
                  initial={{ opacity: 0, y: 30, scale: 0.94 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true, amount: 0.3 }}
                  whileHover={{ y: -12, scale: 1.045, rotate: -1 }}
                  transition={{ type: "spring", stiffness: 110, damping: 16 }}
                />
              ) : (<>

              <div className="absolute w-40 h-40 md:w-48 md:h-48 rounded-full bg-gradient-to-br from-white via-[#d9f5ff] to-[#7cc8e6] shadow-[inset_-12px_-15px_25px_rgba(14,79,132,0.16),inset_10px_12px_20px_rgba(255,255,255,0.95),0_20px_30px_rgba(12,84,127,0.12)]" />
              <div className="absolute w-32 h-32 md:w-40 md:h-40 rounded-full border border-[#2ab5cf]/20 rotate-12" />
              <div className="relative rounded-[2rem] p-5 bg-gradient-to-br from-white/80 via-[#d8f1fa]/60 to-[#7fbdd5]/50 shadow-[inset_3px_3px_8px_rgba(255,255,255,0.9),inset_-6px_-8px_14px_rgba(14,89,143,0.17),12px_20px_22px_rgba(18,41,77,0.18)] -rotate-6">
                <service.icon size={140} strokeWidth={1.25} className="text-[#087cac] w-24 h-24 md:w-32 md:h-32 drop-shadow-[4px_6px_2px_rgba(5,67,106,0.25)]" />
                <div className="absolute -bottom-5 -right-8 rounded-2xl bg-white shadow-lg p-3 border border-[#d6edf4]">
                  <service.accent size={42} strokeWidth={1.5} className="text-[#2ab5cf]" />
                </div>
              </div>
              </>)}
            </div>
          </Reveal>
        </div>
        )}
      </div>
    ))}
  </div>
);

export default function ServiceScenes() {
  return (
    <section id="services" className="relative" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal className="pt-28 md:pt-36 max-w-3xl">
          <h2 className="font-display text-[#12294d] text-4xl md:text-6xl tracking-tight" data-testid="services-heading">
            02 — Our <span className="text-[#2ab5cf]">Services</span>
          </h2>
          <p className="overline-label mt-8" data-testid="services-overline">
            Comprehensive Health Screening • Risk Assessment • Employee Wellness Programs
          </p>
        </Reveal>
        <AdditionalServices services={[moreServices[0]]} offset={0} />
        <PehcScene />
        <AhcScene />
        <NutritionScene />
        <MentalScene />
        <AdditionalServices services={[moreServices[1]]} offset={5} />
        <AdditionalServices services={moreServices.slice(2)} offset={6} />
      </div>
    </section>
  );
}
